import React, { useState, useEffect, useRef, useCallback } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db, auth } from "../firebase";
import useServerClock from "../hooks/useServerClock";
import { useAuthState } from "react-firebase-hooks/auth";
import getServerOffset from "../utils/getServerOffset";

export default function OnlinePlayersButton({ onSelectedPlayer, isTab, currentUserName }) {
  const [authUser] = useAuthState(auth);
  useServerClock();
  const [showList, setShowList] = useState(false);
  const [onlinePlayers, setOnlinePlayers] = useState([]);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const callUpdateOnline = useCallback(async () => {
    try {
      const token = await authUser?.getIdToken?.();
      if (!token) return;

      await fetch("https://updateonline-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error("Failed to update online status:", err);
    }
  }, [authUser]);

  useEffect(() => {
    if (!authUser) return;

    callUpdateOnline();

    const interval = setInterval(() => {
      callUpdateOnline();
    }, 4 * 60 * 1000); 

    return () => clearInterval(interval);
  }, [authUser, callUpdateOnline]);

useEffect(() => {
  if (!authUser) return;

  let unsub = () => {};

  const setupListener = async () => {
    const offset = await getServerOffset();
    
    const currentServerTime = Date.now() + offset;

    const fiveMinutesAgoServer = currentServerTime - (5 * 60 * 1000);

    const q = query(
      collection(db, "Online_Players"),
      where("onlineTimestamp", ">", fiveMinutesAgoServer)
    );

    unsub = onSnapshot(q, (snapshot) => {

      let players = snapshot.docs.map(doc => ({
        id: doc.id,
        displayName: doc.data().onlineDisplayName || doc.id
      }));

      const meFound = players.some(p => p.id === authUser.uid);
      if (!meFound) {
        players.push({
          id: authUser.uid,
          displayName: currentUserName || "Loading..."
        });
      }

      const uniquePlayers = Array.from(new Map(players.map(p => [p.id, p])).values());
      uniquePlayers.sort((a, b) => a.displayName.localeCompare(b.displayName));
      setOnlinePlayers(uniquePlayers);
    }, (err) => {
      console.error("Firestore Listener Error:", err);
    });
  };

  setupListener();

  return () => unsub();
}, [authUser, currentUserName]);

  useEffect(() => {
    if (!showList) return;
    const handleClickOutside = e => {
      if (!buttonRef.current?.contains(e.target) && !dropdownRef.current?.contains(e.target)) {
        setShowList(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showList]);

  if (!authUser) return null;

  return (
    <div style={{ flex: isTab ? 1 : "0 1 auto", position: "relative", display: "flex" }}>
      <button
        ref={buttonRef}
        onClick={() => setShowList(prev => !prev)}
        style={{
          flex: 1,
          background: "transparent",
          border: "none",
          padding: "10px 0",
          fontSize: "0.6rem",
          letterSpacing: "1px",
          cursor: "pointer",
          textTransform: "uppercase",
          fontFamily: "inherit",
          transition: "all 0.2s ease",
          borderBottom: `2px solid ${showList ? "#50e3c2" : "transparent"}`,
          color: showList ? "#50e3c2" : "#555",
          textAlign: "center"
        }}
      >
        Online ({onlinePlayers.length})
      </button>

      {showList && (
        <div
          ref={dropdownRef}
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            width: "160px",
            background: "#121212",
            border: "1px solid rgba(80, 227, 194, 0.2)",
            zIndex: 1000,
            boxShadow: "0 4px 15px rgba(0,0,0,0.8)",
            padding: "8px 0"
          }}
        >
          <div style={{ fontSize: "0.6rem", color: "#50e3c2", padding: "4px 12px", borderBottom: "1px solid rgba(255,255,255,0.05)", marginBottom: "4px", letterSpacing: "1px" }}>
            ACTIVE USERS
          </div>

          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            {onlinePlayers.length > 0 ? (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {onlinePlayers.map(player => (
                  <li key={player.id}>
                    <button
                      style={{ width: "100%", textAlign: "left", background: "transparent", border: "none", padding: "8px 12px", color: "#ccc", fontSize: "0.7rem", cursor: "pointer", transition: "all 0.1s ease" }}
                      onMouseOver={(e) => { e.target.style.background = "rgba(80, 227, 194, 0.1)"; e.target.style.color = "#50e3c2"; }}
                      onMouseOut={(e) => { e.target.style.background = "transparent"; e.target.style.color = "#ccc"; }}
                      onClick={() => {
                        onSelectedPlayer(player.displayName);
                        setShowList(false);
                      }}
                    >
                      {player.displayName}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ padding: "10px", fontSize: "0.65rem", color: "#444", textAlign: "center" }}>
                Empty
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}